import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddLevelStore} from './parts/AddLevelStore';
import {DeleteLevelStore} from './parts/DeleteLevelStore';
import {EditLevelStore} from './parts/EditLevelStore';
import {MoveLevelStore} from './parts/MoveLevelStore';
import {ReorderLevelsStore} from './parts/ReorderLevelsStore';
import {X2AgeCategoryLoader} from './X2AgeCategoryLoader';

type Mode = 'View' | 'Add' | 'Edit' | 'Delete' | 'ReOrder' | 'Move';


export class X2AgeCategoryStore {
  readonly loader: X2AgeCategoryLoader;
  @observable
  mode: Mode;

  @observable.ref
  addLevel: AddLevelStore | null;
  @observable.ref
  editLevel: EditLevelStore | null;
  @observable.ref
  deleteLevel: DeleteLevelStore | null;
  @observable.ref
  reorder: ReorderLevelsStore | null;
  @observable.ref
  move: MoveLevelStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly ageCategoryId: number) {
    makeObservable(this);
    this.mode = 'View';

    this.loader = new X2AgeCategoryLoader(ageCategoryId);
    this.disposers.add(this.loader);
    this.addLevel = this.editLevel = this.deleteLevel =
      this.move = this.reorder = null;
    this.setup();
  }

  @action
  private clear = () => {
    this.mode = 'View';
    this.addLevel = this.editLevel = this.deleteLevel =
      this.move = this.reorder = null;
  };

  @computed
  get isSaving(): boolean {
    return (this.addLevel?.save?.isPending === true ||
      this.editLevel?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.deleteLevel?.save?.isPending === true ||
      this.move?.save?.isPending === true);
  }

  @action
  beginAdd = () => {
    this.mode = 'Add';
    this.addLevel = new AddLevelStore(this.ageCategoryId);
  };

  @action
  completeAdd = () => {
    this.addLevel?.complete();
  };

  @action
  beginEdit = (levelId: number) => {
    this.mode = 'Edit';
    let level = this.loader.levelById[levelId]!;
    this.editLevel = new EditLevelStore(level);
  };

  @action
  completeEdit = () => {
    this.editLevel?.complete();
  };

  @action
  completeOnEnter = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'Add')
      this.completeAdd();
    if (this.mode === 'Edit')
      this.completeEdit();
  };

  @action
  beginMove = (levelId: number) => {
    this.mode = 'Move';
    const level = this.loader.levelById[levelId];
    this.move = new MoveLevelStore(level, this.loader.lookup!);
  };

  @action
  completeMove = () => {
    this.move?.complete();
  };

  @action
  beginDelete = (levelId: number) => {
    this.mode = 'Delete';
    this.deleteLevel = new DeleteLevelStore(levelId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteLevel?.complete();
  };

  @action
  beginReorder = () => {
    this.mode = 'ReOrder';
    const levelIds = this.loader.levels.map(x => x.Id);
    this.reorder = new ReorderLevelsStore(this.ageCategoryId, levelIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorder?.swap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorder?.swap(index, index + 1);
  };

  @action
  completeReorder = () => {
    this.reorder?.complete();
  };

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.clear();
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addLevel?.save?.state,
        this.editLevel?.save?.state,
        this.deleteLevel?.save?.state,
        this.reorder?.save?.state,
        this.move?.save?.state
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addLevel?.save,
      this.editLevel?.save,
      this.deleteLevel?.save,
      this.reorder?.save,
      this.move?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.refresh();
        this.clear();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
